import axios from 'axios';
import Cookies from 'js-cookie';
import {
  GeneralInfo,
  StickyData,
  SpecificView,
  ArchiveStatus,
  Section1View,
  Section21View,
  Section3View,
  Section4View,
  Section6View,
  DfNoticeSectionFiles,
  Clarifications,
  Errata,
} from '../models/CN/models';

import { ADVForView, AllVersions } from '../models/ADV/models';
import { PINoticeDocument } from '../models/PI/models';

const BASE_URL = import.meta.env.VITE_BASE_URL;

// Build request headers, including the JWT from the auth cookie when available.
// Anonymous calls keep the same shape; authenticated calls (admin / paying users) get
// full content from PartialContentFilter and pass AuthorizeBySubscriptionLevel.
const buildAuthHeaders = (extra = {}) => {
  const token = Cookies.get('jwtToken');
  return {
    'Content-Type': 'application/json',
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
const SEARCH_API_URL = `${BASE_URL}/api/ContractListWeb/SearchNoticesAsync`;
const NOTICE_DETAILS_BY_ID_API_URL = `${BASE_URL}/api/ContractListWeb/GetNoticeDetail`;
const NOTICE_ADDITIONAL_DATA_API_URL = `${BASE_URL}/api/ContractListWeb/GetNoticeAdditionalData`;

const SEARCH_DIRECT_AWARD_API_URL = `${BASE_URL}/api/DirectAwardListWeb/SearchDirectAwardNoticesAsync`;
const SEARCH_PI_NOTICE_API_URL = `${BASE_URL}/api/PINoticeListWeb/SearchPINoticesAsync`;

/**
 * Helper function to clean filters and remove null/undefined/empty values
 * @param {Object} filters - Raw filters object
 * @returns {Object} - Cleaned filters suitable for query parameters
 */
const cleanFiltersForAPI = (filters) => {
  const cleaned = {};

  Object.entries(filters).forEach(([key, value]) => {
    // Skip null, undefined, empty strings
    if (value === null || value === undefined || value === '') {
      return;
    }

    // Parse numeric values
    if (key === 'minEstimatedValue' || key === 'maxEstimatedValue') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        cleaned[key] = parsed;
      }
      return;
    }

    // Parse date values
    if (key === 'publishedDate' && value) {
      cleaned[key] = value;
      return;
    }

    // Parse pageIndex and pageSize
    if (key === 'pageIndex' || key === 'pageSize') {
      const parsed = parseInt(value);
      if (!isNaN(parsed)) {
        cleaned[key] = parsed;
      }
      return;
    }

    // Keep all other non-empty values
    cleaned[key] = value;
  });

  return cleaned;
};

/**
 * Obține rezultatele căutării utilizând filtre curate (GET endpoint pentru SEO)
 * @param {Object} filters - Filtrele de căutare trimise la API
 * @returns {Promise<Object>} - Rezultatele căutării
 */
export const fetchSearchResults = async (filters) => {
  console.log('Filtre trimise la API:', filters);

  const cleanedFilters = cleanFiltersForAPI(filters);

  try {
    // Apelul API pentru căutare (GET pentru SEO-friendly URLs)
    const response = await axios.get(SEARCH_API_URL, {
      params: cleanedFilters,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      'A apărut o problemă cu operația de căutare:',
      error.response ? error.response.data : error.message,
    );
    throw error;
  }
};

/**
 * Obține detalii despre o notificare specifică după numărul acesteia
 * @param {string} noticeNo - Numărul unic al notificării
 * @returns {Promise<Object>} - Detalii despre notificare mapate
 */
export const fetchNoticeDetails = async (id) => {
  if (!id) {
    throw new Error(
      'ID-ul notificării este obligatoriu pentru obținerea detaliilor.',
    );
  }

  try {
    const { data } = await axios.get(`${NOTICE_DETAILS_BY_ID_API_URL}/${id}`, {
      headers: buildAuthHeaders(),
    });

    // Maparea răspunsului API în modele
    return {
      isPartial: data.IsPartial || false,
      generalInfo: data.GeneralInfo ? new GeneralInfo(data.GeneralInfo) : null,
      stickyData: data.StickyData ? new StickyData(data.StickyData) : null,
      specificView: data.SpecificView
        ? new SpecificView(data.SpecificView)
        : null,
      archiveStatus: data.ArchiveStatus
        ? new ArchiveStatus(data.ArchiveStatus)
        : null,
      section1View: data.Section1View
        ? new Section1View(data.Section1View)
        : null,
      section21View: data.Section21View
        ? new Section21View(data.Section21View)
        : null,
      section3View: data.Section3View
        ? new Section3View(data.Section3View)
        : null,
      section4View: data.Section4View
        ? new Section4View(data.Section4View)
        : null,
      section6View: data.Section6View
        ? new Section6View(data.Section6View)
        : null,
      // Datele suplimentare vor fi null inițial și se vor încărca separat
      dfNoticeSectionFiles: null,
      clarifications: null,
      errata: null,
    };
  } catch (error) {
    console.error(
      'Eroare la obținerea detaliilor notificării:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Obține datele suplimentare pentru o notificare (files, clarifications, errata)
 * Apelat separat pentru progressive loading
 * @param {string} noticeNo - Numărul notificării
 * @returns {Promise<Object>} - Datele suplimentare mapate
 */
export const fetchNoticeAdditionalData = async (noticeNo) => {
  if (!noticeNo) {
    throw new Error('Numărul notificării este obligatoriu.');
  }

  try {
    const { data } = await axios.get(
      `${NOTICE_ADDITIONAL_DATA_API_URL}/${noticeNo}`,
      {
        headers: buildAuthHeaders(),
      },
    );

    return {
      dfNoticeSectionFiles: data.DfNoticeSectionFiles
        ? new DfNoticeSectionFiles(data.DfNoticeSectionFiles)
        : null,
      clarifications: data.Clarifications
        ? new Clarifications(data.Clarifications)
        : null,
      errata: data.Errata ? new Errata(data.Errata) : null,
    };
  } catch (error) {
    console.error(
      'Eroare la obținerea datelor suplimentare:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Obține rezultatele căutării pentru achiziții directe (GET endpoint pentru SEO)
 * @param {Object} filters - Filtrele de căutare trimise la API
 * @returns {Promise<Object>} - Rezultatele căutării
 */
export const fetchDirectAwardSearchResults = async (filters) => {
  console.log('Filtre trimise la API:', filters);

  const cleanedFilters = cleanFiltersForAPI(filters);

  try {
    // Apelul API pentru căutare (GET pentru SEO-friendly URLs)
    const response = await axios.get(SEARCH_DIRECT_AWARD_API_URL, {
      params: cleanedFilters,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      'A apărut o problemă cu operația de căutare:',
      error.response ? error.response.data : error.message,
    );
    throw error;
  }
};

/**
 * Obține detalii despre o notificare specifică după numărul acesteia
 * @param {string} noticeNo - Numărul unic al notificării
 * @returns {Promise<Object>} - Detalii despre notificare mapate
 */
export const fetchDirectAwardDetails = async (id) => {
  if (!id) {
    throw new Error(
      'ID-ul notificării este obligatoriu pentru obținerea detaliilor.',
    );
  }

  try {
    const { data } = await axios.get(
      `${BASE_URL}/api/DirectAwardListWeb/GetDirectAwardDetail/${id}`,
      {
        headers: buildAuthHeaders(),
      },
    );

    // Maparea răspunsului API în modele
    return {
      isPartial: data.IsPartial || false,
      advForView: data.AdvForView ? new ADVForView(data.AdvForView) : null,
      AllVersions: data.AllVersions ? new AllVersions(data.AllVersions) : null,
      specificView: data.SpecificView
        ? new SpecificView(data.SpecificView)
        : null,
      dfNoticeSectionFiles: data.DfNoticeSectionFiles
        ? new DfNoticeSectionFiles(data.DfNoticeSectionFiles)
        : null,
    };
  } catch (error) {
    console.error(
      'Eroare la obținerea detaliilor notificării:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Descarcă un document din notificare
 * @param {string} noticeDocumentUrl - URL-ul documentului
 * @param {string} noticeDocumentName - Numele documentului
 * @param {string} cNoticeId - ID-ul notificării CN
 * @param {string} noticeNo - Numărul notificării
 * @returns {Promise<Blob>} - Blob-ul fișierului
 */
export const downloadNoticeDocument = async (
  noticeDocumentUrl,
  noticeDocumentName,
  cNoticeId,
  noticeNo,
) => {
  if (!noticeDocumentUrl || !noticeDocumentName || !cNoticeId || !noticeNo) {
    throw new Error(
      'Toți parametrii sunt obligatori pentru descărcarea documentului.',
    );
  }

  try {
    const url = `${
      BASE_URL
    }/api/SU_ContractList/download/?noticeDocumentUrl=${encodeURIComponent(
      noticeDocumentUrl,
    )}&noticeDocumentName=${encodeURIComponent(
      noticeDocumentName,
    )}&cNoticeId=${cNoticeId}&noticeNo=${encodeURIComponent(noticeNo)}`;

    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    });

    return {
      blob: response.data,
      fileName: noticeDocumentName.replace(/\.p7m|\.p7s$/, ''),
      contentDisposition: response.headers['content-disposition'],
    };
  } catch (error) {
    console.error(
      'Eroare la descărcarea documentului notificării:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Descarcă o clarificare din notificare
 * @param {string} noticeDocumentUrl - URL-ul documentului
 * @param {string} fileName - Numele fișierului
 * @param {string} cNoticeId - ID-ul notificării CN
 * @param {string} noticeNo - Numărul notificării
 * @returns {Promise<Blob>} - Blob-ul fișierului
 */
export const downloadClarification = async (
  noticeDocumentUrl,
  fileName,
  cNoticeId,
  noticeNo,
) => {
  if (!noticeDocumentUrl || !fileName || !cNoticeId || !noticeNo) {
    throw new Error(
      'Toți parametrii sunt obligatori pentru descărcarea clarificării.',
    );
  }

  try {
    const endpoint =
      `${BASE_URL}/api/ContractListWeb/DownloadClarification` +
      `?noticeDocumentUrl=${encodeURIComponent(noticeDocumentUrl)}` +
      `&fileName=${encodeURIComponent(fileName)}` +
      `&cNoticeId=${encodeURIComponent(cNoticeId)}` +
      `&noticeNo=${encodeURIComponent(noticeNo)}`;

    const response = await axios.get(endpoint, {
      responseType: 'blob',
      headers: { Accept: 'application/octet-stream' },
    });

    return {
      blob: response.data,
      fileName: fileName.replace(/\.p7m|\.p7s$/, ''),
      contentDisposition: response.headers['content-disposition'],
    };
  } catch (error) {
    console.error(
      'Eroare la descărcarea clarificării:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Descarcă un fișier de errata din notificare
 * @param {string} noticeDocumentUrl - URL-ul documentului
 * @param {string} fileName - Numele fișierului
 * @param {string} cNoticeId - ID-ul notificării CN
 * @param {string} noticeNo - Numărul notificării
 * @returns {Promise<Blob>} - Blob-ul fișierului
 */
export const downloadErrata = async (
  noticeDocumentUrl,
  fileName,
  cNoticeId,
  noticeNo,
) => {
  if (!noticeDocumentUrl || !fileName || !cNoticeId || !noticeNo) {
    throw new Error(
      'Toți parametrii sunt obligatori pentru descărcarea errata-ului.',
    );
  }

  try {
    const endpoint =
      `${BASE_URL}/api/ContractListWeb/DownloadErrata` +
      `?noticeDocumentUrl=${encodeURIComponent(noticeDocumentUrl)}` +
      `&fileName=${encodeURIComponent(fileName)}` +
      `&cNoticeId=${encodeURIComponent(cNoticeId)}` +
      `&noticeNo=${encodeURIComponent(noticeNo)}`;

    const response = await axios.get(endpoint, {
      responseType: 'blob',
      headers: { Accept: 'application/octet-stream' },
    });

    return {
      blob: response.data,
      fileName: fileName.replace(/\.p7m|\.p7s$/, ''),
      contentDisposition: response.headers['content-disposition'],
    };
  } catch (error) {
    console.error(
      'Eroare la descărcarea errata-ului:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Trigger pentru descărcarea unui fișier (utilitare pentru UI)
 * @param {Blob} blob - Blob-ul fișierului
 * @param {string} fileName - Numele fișierului
 */
export const triggerFileDownload = (blob, fileName) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

// ============================================================
// PI (Anunturi de Intentie) API Functions
// ============================================================

/**
 * Cautare anunturi de intentie cu filtre
 */
export const fetchPINoticeSearchResults = async (filters = {}) => {
  const cleanedFilters = cleanFiltersForAPI(filters);
  const response = await axios.get(SEARCH_PI_NOTICE_API_URL, {
    params: cleanedFilters,
    headers: { 'Content-Type': 'application/json' },
  });
  return {
    results: response.data.results || [],
    totalResults: response.data.totalResults || 0,
    pageIndex: response.data.pageIndex || 0,
    pageSize: response.data.pageSize || 10,
  };
};

/**
 * Live refresh of ADV (achizitie directa) view — DocumentUrl/DocumentName/DocumentID
 * rotate on e-licitatie.ro and go stale in our MongoDB cache, breaking downloads.
 * The page calls this endpoint after the initial render so the download buttons use
 * fresh values. Subscription-gated server-side.
 */
export const fetchDirectAwardAdditionalData = async (id) => {
  if (!id) {
    throw new Error('ID-ul achizitiei directe este obligatoriu.');
  }

  const { data } = await axios.get(
    `${BASE_URL}/api/DirectAwardListWeb/GetDirectAwardAdditionalData/${id}`,
    { headers: buildAuthHeaders() },
  );

  return {
    advForView: data.AdvForView ? new ADVForView(data.AdvForView) : null,
  };
};

/**
 * Detalii anunt de intentie
 */
export const fetchPINoticeDetails = async (id) => {
  const response = await axios.get(`${BASE_URL}/api/PINoticeListWeb/GetPINoticeDetail/${id}`, {
    headers: buildAuthHeaders(),
  });
  const result = new PINoticeDocument(response.data);
  result.isPartial = response.data.IsPartial || false;
  return result;
};
